import React, { useState } from 'react';
import { folderService } from '@/services/folderService';
import { Folder } from '@/types';
import toast from 'react-hot-toast';
import DangerButton from '@/Components/DangerButton';
import SecondaryButton from '@/Components/SecondaryButton';
import Modal from '@/Components/Modal';
import TrashIcon from '@/Components/Icons/TrashIcon';

interface FolderDeleteModalProps {
    show: boolean;
    folder: Folder | null;
    onClose: () => void;
    onFolderDeleted: (folderId: string) => void;
}

export default function FolderDeleteModal({ show, folder, onClose, onFolderDeleted }: FolderDeleteModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleDelete = async () => {
        if (!folder) return;

        setIsSubmitting(true);

        try {
            await folderService.deleteFolder(folder.id);
            toast.success('Folder deleted successfully');
            onFolderDeleted(folder.id);
            onClose();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to delete folder';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onClose={onClose} maxWidth="md">
            <div className="p-6">
                <div className="flex items-center mb-4">
                    <div className="">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">
                            Delete Folder
                        </h3>
                    </div>
                </div>

                <div className="mb-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Are you sure you want to delete the folder "{folder?.name}"?
                        This action cannot be undone. Jobs in this folder will be moved to "All Files".
                    </p>
                </div>

                <div className="flex justify-end">
                    <SecondaryButton
                        type="button"
                        className="me-2"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </SecondaryButton>
                    <DangerButton
                        type="button"
                        onClick={handleDelete}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Deleting...' : 'Delete Folder'}
                    </DangerButton>
                </div>
            </div>
        </Modal>
    );
}
